import { InteractionDto } from "@/repository/models";
import {
	TableColumnDefinition,
	TableColumnId,
	createTableColumn,
} from "@fluentui/react-components";
import { ReactNode } from "react";

export enum WaitingInteractionsTableColumnNames {
	Icon = "Icon",
	Customer = "Customer",
	Queue = "Queue",
	Skills = "Skills",
	Tags = "Tags",
	Timer = "Timer",
	Agent = "Agent",
}

export enum ActiveInteractionsTableColumnName {
	Icon = "Icon",
	Customer = "Customer",
	Queue = "Queue",
	Skills = "Skills",
	Tags = "Tags",
	Agent = "AgentAssigned",
	Timer = "Timer",
	Sentiment = "Sentiment",
	Actions = "Actions",
}

export enum TableType {
	None,
	Waiting,
	Active,
	Voicemail,
	SupervisorAgents,
}

export interface StaticTableColumnInfo {
	name: string;
	label?: string;
	width: string;
	leftOffset?: string;
}

export interface IdentifiableTableColumnInfo {
	columnId: string | TableColumnId;
}

declare interface CommonTableColumnInfo extends IdentifiableTableColumnInfo {
	fieldName?: string;
	width?: string;
	minWidth?: number;
	idealWidth?: number;
	notResizable?: boolean;
	notSortable?: boolean;
	notRemovable?: boolean;
	hideHeader?: boolean;
	customRenderCell?: (item: any, hoveredItemKey: string | null) => ReactNode;
}

export interface TableColumnInfo extends CommonTableColumnInfo {
	sortingReversed?: boolean;
	renderCell?: (item: any) => ReactNode;
	compare?: (itemA: any, itemB: any) => number;
}

export interface CustomTableColumnDefinition<TItem>
	extends TableColumnDefinition<TItem>,
		CommonTableColumnInfo {}

/**
 * Column widths for the waiting table. Six columns cannot keep the five-column widths — Skills plus
 * Tags would total 116% and over-constrain the table — so the whole set is rebalanced when tags are in
 * play. Tenants with no tag configured keep the exact widths the table shipped with.
 */
const waitingColumnWidths = {
	withTags: {
		customer: "20%",
		queue: "13%",
		skills: "16%",
		tags: "16%",
		agent: "17%",
		timer: "18%",
	},
	withoutTags: {
		customer: "25%",
		queue: "15%",
		skills: "20%",
		tags: "0%",
		agent: "18%",
		timer: "18%",
	},
};

export const getWaitingTableColumnInfo = (
	hasAnyQueueSkillRoutingEnabled: boolean,
	tagsExist: boolean = false
): StaticTableColumnInfo[] => {
	const widths = tagsExist
		? waitingColumnWidths.withTags
		: waitingColumnWidths.withoutTags;

	return [
		{
			name: WaitingInteractionsTableColumnNames.Customer,
			width: widths.customer,
			leftOffset: "49px",
		},
		{
			name: WaitingInteractionsTableColumnNames.Queue,
			width: widths.queue,
		},
		...(hasAnyQueueSkillRoutingEnabled
			? [
					{
						name: WaitingInteractionsTableColumnNames.Skills,
						width: widths.skills,
					},
				]
			: []),
		...(tagsExist
			? [
					{
						name: WaitingInteractionsTableColumnNames.Tags,
						width: widths.tags,
					},
				]
			: []),
		{
			name: WaitingInteractionsTableColumnNames.Agent,
			width: widths.agent,
		},
		{
			name: WaitingInteractionsTableColumnNames.Timer,
			width: widths.timer,
		},
	];
};

/**
 * Width of a waiting-table column by name. The columns are conditional — Skills only when a queue uses
 * skill-based routing, Tags only when the tenant has one — so their positions shift. Addressing them by
 * index meant every optional column doubled the arithmetic on the ones after it, and a wrong index
 * rendered `width: undefined` rather than failing.
 */
export const getWaitingTableColumnWidth = (
	columnInfo: StaticTableColumnInfo[],
	name: WaitingInteractionsTableColumnNames
): string | undefined => {
	return columnInfo.find((column) => column.name === name)?.width;
};

export const waitingTableColumnInfo: StaticTableColumnInfo[] = [
	{
		name: WaitingInteractionsTableColumnNames.Customer,
		width: "25%",
		leftOffset: "49px",
	},
	{
		name: WaitingInteractionsTableColumnNames.Queue,
		width: "15%",
	},
	{
		name: WaitingInteractionsTableColumnNames.Skills,
		width: "20%",
	},
	{
		name: WaitingInteractionsTableColumnNames.Timer,
		width: "18%",
	},
];

const waitingInteractionsTableColumns: string[] = Object.keys(
	WaitingInteractionsTableColumnNames
).map(String);

export const waitingInteractionsTableColumnDefinitions: TableColumnDefinition<InteractionDto>[] =
	waitingInteractionsTableColumns.map((x) =>
		createTableColumn<InteractionDto>({
			columnId: x,
		})
	);

export const generateColumnsDefinitionArray = <T>(
	columnInfo: TableColumnInfo[]
): CustomTableColumnDefinition<T>[] => {
	return columnInfo.map((x) => {
		return {
			...x,
			...createTableColumn<T>({
				...x,
				compare: x.compare
					? x.compare
					: (a, b) => {
							if (x.fieldName && !x.notSortable) {
								let fieldA = (a[x.fieldName as keyof T] ?? "") as string;
								let fieldB = (b[x.fieldName as keyof T] ?? "") as string;

								if (x.sortingReversed) {
									return fieldB.localeCompare(fieldA);
								}

								return fieldA.localeCompare(fieldB);
							}

							return 0;
						},
			}),
		};
	});
};
